import ButtonType from './buttonType';
import ButtonVariant from './buttonVariant';
import bemify from '../../../utils/bemUtils';
import Component from '../../component/component';

/**
 * Represents a button component with a consistent look and feel across browsers.
 */
class Button extends Component {
  #buttonRef = null;

  /**
   * Gets a value that determines whether the ripple effect will show when the component is clicked.
   */
  get allowRipple() {
    return this.getAttribute('allow-ripple') === 'false' ? false : true;
  }

  /**
   * Gets a value that determines whether the component is disabled.
   */
  get disabled() {
    return this.hasAttribute('disabled');
  }

  /**
   * Sets a value that determines whether the component is disabled.
   */
  set disabled(value) {
    if (value) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  /**
   * Gets the href for the component.
   */
  get href() {
    return this.getAttribute('href');
  }

  /**
   * Gets the type of component.
   */
  get type() {
    return this.getAttribute('type') ?? ButtonType.button;
  }

  /**
   * Gets the variant of the component.
   */
  get variant() {
    return this.getAttribute('variant') ?? ButtonVariant.primary;
  }

  initTemplate() {
    const [block] = bemify('button');
    const template = this.createTemplate();
    template.innerHTML += `
      ${
        this.href
          ? `
        <a
          ${this.addAttributeMarkup('id', this.id)}
          class="${block(this.variant, this.disabled ? 'disabled' : '')}"
          href="${this.href}"
          part="button"
          ${this.addAttributeMarkup('title', this.title)}>
          <slot></slot>
        </a>
        `
          : ''
      }
      ${
        !this.href
          ? `
          <button
            ${this.addAttributeMarkup('id', this.id)}
            class="${block(this.variant)}"
            ${this.addAttributeMarkup('disabled', this.disabled)}
            part="button"
            ${this.addAttributeMarkup('title', this.title)}
            type="${this.type}">
            <slot></slot>
          </button>
        `
          : ''
      }
    `;

    this.initShadowRoot(template.content.cloneNode(true));
  }

  onMounted() {
    this.#buttonRef = this.shadowRoot.querySelector('[part="button"]');
    this.#buttonRef?.addEventListener('click', e => this.#onClick(e));
  }

  /**
   * Called once to return the attributes to observe.
   */
  static get observedAttributes() {
    return ['disabled'];
  }

  onAttributeChange() {
    if (this.disabled) {
      if (this.href) {
        this.#buttonRef?.classList.add('button--disabled');
        return;
      }

      this.#buttonRef?.setAttribute('disabled', '');
      return;
    }

    this.#buttonRef?.classList.remove('button--disabled');
    this.#buttonRef?.removeAttribute('disabled');
  }

  #onClick(e) {
    if (this.allowRipple) {
      const { clientX, clientY } = e;
      const actualButtonRef = this.#buttonRef;
      const buttonBounds = actualButtonRef.getBoundingClientRect();
      const { width, height, left, top } = buttonBounds;
      const diameter = Math.max(width, height);
      const radius = diameter / 2;
      const newRipple = document.createElement('span');
      newRipple.style.width = newRipple.style.height = `${diameter}px`;
      newRipple.style.left = `${clientX - (left + radius)}px`;
      newRipple.style.top = `${clientY - (top + radius)}px`;
      newRipple.className = `button__ripple ${
        this.variant === ButtonVariant.primary ? 'button__ripple--primary' : 'button__ripple--secondary'
      }`;

      const existingRipple = actualButtonRef.querySelector('.button__ripple');
      existingRipple?.remove();

      actualButtonRef.appendChild(newRipple);
    }

    this.fireCustomEvent('on-click', { target: e.target });
  }
}

customElements.define('sk-button', Button);

export default Button;
